import { useState, memo, useEffect } from 'react';
import { useEdges, useReactFlow } from 'reactflow';
import { useNotificationStore } from '../../store/storeNotifications';
import { useAuthStore } from '../../store/storeAuth';
import { useParams } from 'react-router-dom';
import { NodeProps } from '../../util/Types/NodeProps';
import AutoTriggerTimerModal from '../CoreNodeDIsplay/AutoTriggerTimerModal';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { Gear, GlobeHemisphereEast, Sticker } from '@phosphor-icons/react';
import OutputHandle from '../UI/Handle/OutputHandle';
import Label from '../UI/Label/Label';
import { isActiveEdge } from '../../util/findActiveHandle';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import SettingsModal from '../FlowGlobalNode/SettingsModal/SettingsModal';
import { findConnectedNodes } from '../../util/executeNode/findConnectedNodes';
import { FlowService } from '../../service/FlowService';

function FlowGlobalNode({ id, data }: NodeProps) {
  const { folders } = useAuthStore(state => state);
  const { flowId } = useParams();
  const activeFolder =
    folders?.find(folder => {
      const activeFolderByFlowId = folder?.flows?.find(
        flow => flow?.id === flowId,
      );
      return activeFolderByFlowId;
    }) ?? null;
  const edges = useEdges();

  const reactFlow = useReactFlow();

  const { allIds } = findConnectedNodes(reactFlow.getNodes(), edges, id);

  const activeFlow = activeFolder?.flows?.find(flow => flow?.id === flowId);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    setNodeState,
    setSaving,
    setSchema,
    setIsPublic,
    isPublic,
    spaceId,
    setIsWebhook,
    isWebhook,
  } = useFlowsStore(state => state);
  const [isAutoTriggerModalOpen, setIsAutoTriggerModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    notesExpanded: false,
    notes: '',
    loading: false,
    isAutotrigger: false,
    triggerMessage: '',
    generateSessionId: false,
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const [intervalData, setIntervalData] = useState({
    type: 'hourly',
    hourly: '*',
    daily: '*',
    weekly: '*',
    monthly: '*',
    codedTime: '',
    inputText: '',
    varInput: '',
  });

  async function executeFlow() {
    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowTriggerData: {
        flowGlobalId: id,
        flowId: flowId,
      },
      setSaving,
      changeType: 'execute',
      objectCallerData: [
        ...(user?.spaceObjects || []),
        ...(user?.objects || []),
      ],

      setSchema,
      newEdges: edges,
    });

    setFormData({
      ...formData,
      loading: true,
    });
  }

  const onChange = (e: any) => {
    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowId,
      objectData: {
        generateSessionId: !data?.generateSessionId,
      },
      setSaving,
      setSchema,
      newEdges: edges,
    });

    setFormData({
      ...formData,
      generateSessionId: !data?.generateSessionId,
    });
  };

  const saveNode = (data: any) => {
    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowId,
      objectData: {
        ...data,
      },
      setSaving,
      setSchema,
      newEdges: edges,
    });
  };

  const onCancelExecute = () => {
    setNodeState(nds => {
      return nds.map((nd: any) => {
        return {
          ...nd,
          data: {
            ...nd.data,
            flowTrigger: null,
            canceled: true,
          },
        };
      });
    });

    setFormData({
      ...formData,
      loading: false,
    });
  };

  const toggleNotes = () => {
    setFormData({ ...formData, notesExpanded: !formData.notesExpanded });
  };

  const handleChange = (e: any) => {
    if (e.target.name === 'isAutotrigger') {
      if (!formData.isAutotrigger) {
        setIsAutoTriggerModalOpen(true);
      } else {
        setIsAutoTriggerModalOpen(false);
        setFormData(prev => ({
          ...prev,
          triggerMessage: '',
        }));
      }

      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));

      setNodeState(nds =>
        nds.map(nd => {
          if (nd.id === id) {
            return {
              ...nd,
              data: {
                ...nd.data,
                [e.target.name]: e.target.checked,
              },
            };
          }
          return nd;
        }),
      );
    } else {
      setFormData(prev => ({ ...prev, [e.target.name]: e.target.checked }));
    }
  };

  const onUpdateChat = async (data: any, fluxId: string) => {
    try {
      const response = await FlowService.updateNodeData(flowId!, {
        id: fluxId,
        data: {
          ...data,
        },
        changeType: 'default',
      });
    } catch (error) {
      console.log(error);
    }
  };

  const onChangeIsPublicOrWebhook = async (data: any) => {
    try {
      await FlowService.updateFlow(flowId!, {
        public: data.isPublic,
        webhook: data.webhook,
      });

      if ('isPublic' in data) {
        setIsPublic(data.isPublic);
      }
      if ('webhook' in data) {
        setIsWebhook(data.webhook);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (data?.hasFinished) {
      setNotification({
        type: 'success',
        message: 'Flow Global Finished',
      });

      setFormData({
        ...formData,
        loading: false,
      });
      setNodeState(nds => {
        const findFluxObject = nds?.find(
          (nd: any) => nd.type === 'fluxObject' && nd?.data?.chat === true,
        );

        const apiInput: any = nds?.find(
          (nd: any) => nd.type === 'varInputNode',
        );

        const apiOutput: any = nds?.find(
          (nd: any) => nd.type === 'outputObjectNode',
        );

        const text = apiOutput?.data?.textData
          ?.map((item: { text: string }) => item.text)
          ?.join(' ');

        return nds.map((nd: any) => {
          if (nd.type === 'outputObjectNode') {
            return {
              ...nd,
              data: {
                ...nd.data,
                canSendMail: true,
              },
            };
          }

          if (nd.type === 'fluxObject' && findFluxObject?.id === nd.id) {
            let entries = nd?.data?.entries ? nd?.data?.entries : [];

            const selectedChat = nd?.data?.selectedChat || 'default';

            if (selectedChat) {
              const findUserSession = nd?.data?.userSessionsChat?.find(
                (session: any) => session.id === selectedChat,
              );

              if (findUserSession) {
                const userSessionsChat = [
                  ...nd?.data?.userSessionsChat?.map((session: any) => {
                    if (session.id === selectedChat) {
                      return {
                        ...session,
                        entries: [
                          ...session.entries,
                          {
                            role: 'user',
                            content: apiInput?.data?.text,
                            timestamp: new Date().toLocaleString(),
                          },
                          {
                            role: 'assistant',
                            content: text,

                            timestamp: new Date().toLocaleString(),
                          },
                        ],
                      };
                    }
                    return session;
                  }),
                ];
                onUpdateChat(
                  {
                    userSessionsChat,
                    selectedChat,
                  },
                  nd.id,
                );
                return {
                  ...nd,
                  data: {
                    ...nd.data,
                    selectedChat: selectedChat,
                    userSessionsChat,
                  },
                };
              } else {
                const newEntries = [
                  ...entries,
                  ...[
                    {
                      role: 'user',
                      content: apiInput?.data?.text,
                      timestamp: new Date().toLocaleString(),
                    },
                    {
                      role: 'assistant',
                      content: text,
                      timestamp: new Date().toLocaleString(),
                    },
                  ],
                ];

                onUpdateChat(
                  {
                    entries: newEntries,
                  },
                  nd.id,
                );

                return {
                  ...nd,
                  data: {
                    ...nd.data,

                    entries: newEntries,
                  },
                };
              }
            }
          }

          if (nd.id === id) {
            return {
              ...nd,
              data: {
                ...nd.data,
                hasFinished: false,
              },
            };
          }

          return {
            ...nd,
            data: {
              ...nd.data,
              flowTrigger: null,
            },
          };
        });
      });
    } else {
      setFormData({
        ...formData,
        notesExpanded: data.notesExpanded,
        notes: data.notes,
        triggerMessage: data.triggerMessage,
        isAutotrigger: data.isAutotrigger || false,
        generateSessionId: data?.generateSessionId || false,
      });
    }
  }, [data]);

  useEffect(() => {
    setIntervalData(data?.intervalData ?? intervalData);
  }, [data?.intervalData]);

  const { user } = useAuthStore(state => state);

  return (
    <Box
      className={formData.loading ? 'block' : ''}
      sx={{
        bgcolor: user?.theme === 'dark' ? '#1D2939' : 'background.paper',
        p: '16px',
        borderRadius: '8px',
        width: '350px',
      }}
    >
      <Box
        display={{
          display: 'flex',
          justifyContent: 'end',
          gap: '8px',
        }}
      >
        <IconButtonTooltip title={'Open Notes'} onClick={toggleNotes}>
          <Sticker color="#667085" size={20} />
        </IconButtonTooltip>
        <IconButtonTooltip
          onClick={() => {
            setSettingsModalOpen(true);
          }}
          title={'Settings'}
        >
          <Gear color="#667085" size={20} />
        </IconButtonTooltip>
      </Box>
      <Box>
        <GlobeHemisphereEast
          weight="fill"
          size={32}
          color="#667085"
          style={{
            filter: `drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))`,
          }}
        />

        <Label isAdvanced labelName={data.label} />

        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              // color: '#98A2B3',
              color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
              fontSize: '16px',
              mb: '4px',
            }}
          >
            {allIds.length}{' '}
            <span
              style={{
                color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
              }}
            >
              Flow Steps
            </span>
          </Typography>
          <Typography
            sx={{
              color: '#98A2B3',

              fontSize: '16px',
              mb: '4px',
            }}
          >
            {reactFlow?.getNodes()?.length - 1}{' '}
            <span
              style={{
                color: user?.theme === 'dark' ? '#98A2B3' : '#101828',
              }}
            >
              Nodes
            </span>
          </Typography>
        </Box>

        <Box>
          <Typography color={'green'}>Timezone: EST</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isAutotrigger}
                onChange={handleChange}
                name="isAutotrigger"
              />
            }
            label="Auto Trigger"
          />
          {formData.isAutotrigger && (
            <Typography
              style={{ color: 'red' }}
              onClick={() => setIsAutoTriggerModalOpen(true)}
            >
              {formData.triggerMessage}
            </Typography>
          )}
        </Box>

        <Box sx={{}}>
          <Button
            variant="contained"
            onClick={executeFlow}
            fullWidth
            disabled={formData.loading}
          >
            Run Flow
          </Button>

          {formData.loading && (
            <Button
              variant="contained"
              onClick={onCancelExecute}
              fullWidth
              sx={{
                mt: '16px',
              }}
            >
              Cancel Flow
            </Button>
          )}
        </Box>
        <Box style={{ marginTop: 8 }}></Box>

        {formData.notesExpanded && (
          <Box mt={'16px'}>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              name="notes"
              style={{
                height: 100,
                width: 'calc(100% - 16px)',
                padding: 8,
                fontFamily: 'inherit',
                borderRadius: '4px',
                outline: 'none',
                border: `1px solid ${
                  user?.theme === 'dark' ? '#344054' : '#D0D5DD'
                }`,
                backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#fff',
                color: user?.theme === 'dark' ? '#98A2B3' : 'black',
              }}
            />
          </Box>
        )}
      </Box>

      <OutputHandle
        colorActive={{
          background: 'blue',
          color: 'white',
        }}
        handleId="a"
        isActive={isActiveEdge(edges, id, 'a', 'source')}
      />
      <AutoTriggerTimerModal
        isAutoTriggerModalOpen={isAutoTriggerModalOpen}
        setIsAutoTriggerModalOpen={setIsAutoTriggerModalOpen}
        formData={formData}
        setFormData={setFormData}
        data={data}
        saveNode={saveNode}
        intervalData={intervalData}
        setIntervalData={setIntervalData}
        activeFlowId={activeFlow?.id}
        id={id}
      />

      <SettingsModal
        formData={formData}
        onChange={onChange}
        isPublic={isPublic}
        isWebhook={isWebhook}
        onChangeIsPublicOrWebhook={onChangeIsPublicOrWebhook}
        onHide={() => {
          setSettingsModalOpen(false);
        }}
        spaceId={spaceId}
        flowId={flowId}
        show={settingsModalOpen}
      />
    </Box>
  );
}

export default memo(FlowGlobalNode);
